import { useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { hydrateSelection, type SelectionState } from '../../features/booking/selectionSlice'

const VERSION = 1

const storageKey = (slug: string) => `vega.booking.selection.${slug}.v${VERSION}`

function isSelection(value: unknown): value is SelectionState {
  if (!value || typeof value !== 'object') return false
  const v = value as Partial<SelectionState>
  return Array.isArray(v.services) && ('branchId' in v)
}

/**
 * Keeps the cart alive across a refresh. sessionStorage, not localStorage: a
 * half-finished booking should not greet someone a week later, and a shared
 * device should not leak one tab's cart into the next.
 *
 * Returns false until the rehydrate attempt has run, so the step guard does not
 * bounce a refreshed customer back to step 1 before the cart is restored.
 */
export function useSelectionPersistence(slug: string): boolean {
  const dispatch = useAppDispatch()
  const selection = useAppSelector((s) => s.selection)
  const [ready, setReady] = useState(false)
  const hydratedFor = useRef<string | null>(null)

  useEffect(() => {
    if (hydratedFor.current === slug) return
    hydratedFor.current = slug
    setReady(false)
    try {
      const raw = sessionStorage.getItem(storageKey(slug))
      if (raw) {
        const parsed: unknown = JSON.parse(raw)
        if (isSelection(parsed)) dispatch(hydrateSelection(parsed))
      }
    } catch {
      // A blocked or corrupt store is not worth failing a booking over.
    }
    setReady(true)
  }, [slug, dispatch])

  useEffect(() => {
    if (!ready) return
    try {
      sessionStorage.setItem(storageKey(slug), JSON.stringify(selection))
    } catch {
      // Private mode / quota: the wizard still works, it just cannot resume.
    }
  }, [slug, selection, ready])

  return ready
}

export function clearPersistedSelection(slug: string) {
  try {
    sessionStorage.removeItem(storageKey(slug))
  } catch {
    // Nothing to do: the key either never existed or is unreachable.
  }
}
